import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import { setDestroy } from "components/modalx/modalx.slice";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Select,
  Combobox,
  Listbox,
  Tag,
  LegacyStack,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { departmentErrorKey } from "containers/departments/department-errors";
import {
  accessAfterHeadChange,
  canGrantHeadDataAccess,
  countHeadVisiblePeople,
  headAccessBlock,
  headDataAccessPayload,
  HeadDataAccessState,
  isGrantingAccess,
} from "containers/departments/head-data-access";
import { useTranslation } from "react-i18next";
import {
  fetchDepartmentsThunk,
  refreshDepartment,
} from "containers/departments/departments-container.slice";
import {
  createDepartment,
  updateDepartment,
  fetchDepartmentById,
  fetchDepartmentCompanyUsers,
  DepartmentMember,
  DepartmentSubgroup,
} from "services/departments.service";

interface DepartmentFormProps {
  id?: string;
  /** When creating a subgroup, set this to the parent department id. */
  parentDepartment?: string | null;
  /** Called after a successful create/update (before the modal closes). */
  onSaved?: () => void;
  /**
   * Sub-departments of the edited department with their members, when the
   * opener has them loaded — for the «how many people the head will see» note.
   */
  subgroups?: DepartmentSubgroup[];
}

const DepartmentForm: React.FC<DepartmentFormProps> = ({
  id,
  parentDepartment = null,
  onSaved,
  subgroups: subgroupsProp,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const filtersValue = useSelector(
    (state: RootState) => state.departmentsReducer.filtersValue,
  );
  const { role } = useSelector((state: RootState) => state.authReducer);
  const collection = useSelector(
    (state: RootState) => state.departmentsReducer.collection,
  );
  // Only a Master may turn the head's hiring/data access on; anyone may turn it off.
  const canGrant = canGrantHeadDataAccess(role);

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [head, setHead] = useState<string>("");
  const [headHasDataAccess, setHeadHasDataAccess] = useState<boolean>(false);
  // Saved head and whether that head had data access — to tell the user a
  // new head does not inherit it (the backend resets it on a head change too).
  const [savedHead, setSavedHead] = useState<string>("");
  const [savedHeadHadAccess, setSavedHeadHadAccess] = useState<boolean>(false);
  const [parentId, setParentId] = useState<string | null>(
    parentDepartment || null,
  );
  const [memberQuery, setMemberQuery] = useState<string>("");
  const [companyUsers, setCompanyUsers] = useState<DepartmentMember[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Edit stays unsavable until the existing row has loaded so we never PATCH
  // with a null parentDepartment before parentId is known.
  const [isReady, setIsReady] = useState<boolean>(!id);

  // Load selectable company users (keyed by CompaniesUsers id)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const users = await fetchDepartmentCompanyUsers(companyId);
        setCompanyUsers(users);
      } catch (e) {
        setErrMsg("fetch_error");
      }
    })();
  }, [companyId]);

  // Load existing department when editing
  const fetchData = useCallback(
    async (departmentId: string) => {
      if (!companyId) return;
      try {
        const department = await fetchDepartmentById(companyId, departmentId);
        const loadedMemberIds = department.members
          ? department.members.map(
              (member: DepartmentMember) => member.companyUserId,
            )
          : department.memberIds || [];
        const loadedHead =
          typeof department.head === "string"
            ? department.head
            : department.head?.companyUserId || "";
        setName(department.name || "");
        setDescription(department.description || "");
        setMemberIds(loadedMemberIds);
        setHead(loadedHead);
        setHeadHasDataAccess(!!department.headHasDataAccess);
        setSavedHead(loadedHead);
        setSavedHeadHadAccess(!!loadedHead && !!department.headHasDataAccess);
        // Prefer the fetched parent; fall back to the prop so editing a
        // subgroup from the parent detail page cannot clear the link.
        setParentId(department.parentDepartment || parentDepartment || null);
        setIsReady(true);
      } catch (e) {
        setErrMsg("fetch_error");
      }
    },
    [companyId, parentDepartment],
  );

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  // Keep head valid: it must be one of the selected members
  useEffect(() => {
    if (head && !memberIds.includes(head)) {
      setHead("");
      setHeadHasDataAccess(false);
    }
  }, [memberIds, head]);

  const handleToggleMember = useCallback((selected: string | undefined) => {
    if (!selected) return;
    setMemberIds((prev) =>
      prev.includes(selected)
        ? prev.filter((memberId) => memberId !== selected)
        : [...prev, selected],
    );
  }, []);

  const handleRemoveMember = useCallback(
    (memberId: string) => () => {
      setMemberIds((prev) => prev.filter((item) => item !== memberId));
    },
    [],
  );

  const nameFor = (companyUserId: string) =>
    companyUsers.find((u) => u.companyUserId === companyUserId)?.name ||
    companyUserId;

  const accessState: HeadDataAccessState = {
    canGrant,
    head,
    checked: !!head && headHasDataAccess,
    savedHead,
    savedHeadHadAccess,
  };

  const handleFormSubmit = async () => {
    if (!name.trim() || !companyId || !isReady) return;
    const accessPayload = headDataAccessPayload(accessState);
    const formData = {
      id,
      name: name.trim(),
      description,
      head: head || null,
      ...(accessPayload === undefined ? {} : { headHasDataAccess: accessPayload }),
      memberIds,
      parentDepartment: parentId || null,
    };
    const action = id ? updateDepartment : createDepartment;
    try {
      setIsLoading(true);
      setErrMsg(null);
      await action(formData, companyId);
      if (id) {
        // Edit: refresh just this row, keeping loaded pages + scroll position.
        dispatch(refreshDepartment(companyId, id));
      } else {
        // Create: no existing row to patch, so reload the first page.
        dispatch(fetchDepartmentsThunk(companyId, filtersValue));
      }
      setSuccessMessage("ok");
      onSaved?.();
      // Close the modal on success. Without this the modal stayed open and a
      // second Save click created a duplicate department.
      dispatch(setDestroy());
    } catch (e) {
      setErrMsg(departmentErrorKey(e));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCompanyUsers = useMemo(() => {
    const query = memberQuery.trim().toLowerCase();
    if (!query) return companyUsers;
    return companyUsers.filter((user) =>
      `${user.name} ${user.position || ""}`.toLowerCase().includes(query),
    );
  }, [companyUsers, memberQuery]);

  const memberOptionsMarkup = filteredCompanyUsers.map((user) => {
    const isSelected = memberIds.includes(user.companyUserId);
    const label = user.position ? `${user.name} — ${user.position}` : user.name;
    return (
      <Listbox.Option
        key={user.companyUserId}
        value={user.companyUserId}
        selected={isSelected}
        accessibilityLabel={user.name}
      >
        <Listbox.TextOption selected={isSelected}>{label}</Listbox.TextOption>
      </Listbox.Option>
    );
  });

  const accessBlock = headAccessBlock(accessState);
  const accessBlockTitle =
    accessBlock === "master_only"
      ? t("department_head_data_access_master_only")
      : undefined;
  // Known sub-departments: from the opener, else the loaded list row; a new
  // department has none. Unknown → a generic warning instead of a number.
  const knownSubgroups = subgroupsProp
    ? subgroupsProp
    : !id
      ? []
      : collection.find((department) => department.id === id)?.subgroups;
  const visibleCount = countHeadVisiblePeople(memberIds, knownSubgroups);

  const headOptions = [
    { label: t("no_head") || "-", value: "" },
    ...memberIds.map((memberId) => ({
      label: nameFor(memberId),
      value: memberId,
    })),
  ];

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormLayout>
        <div className="form-warnings">
          {errMsg && (
            <div>
              <Banner tone="critical">{t(errMsg)}</Banner>
            </div>
          )}
          {successMessage && (
            <div>
              <Banner tone="info">{t(successMessage)}</Banner>
            </div>
          )}
        </div>

        <TextField
          label={t("name")}
          id="name"
          name="name"
          value={name}
          onChange={(value) => setName(value)}
          autoComplete="off"
        />

        <TextField
          label={t("description")}
          id="description"
          name="description"
          value={description}
          onChange={(value) => setDescription(value)}
          multiline={4}
          autoComplete="off"
        />

        <Combobox
          allowMultiple
          activator={
            <Combobox.TextField
              label={t("members")}
              autoComplete="off"
              value={memberQuery}
              onChange={setMemberQuery}
              placeholder={t("search") || undefined}
              verticalContent={
                memberIds.length > 0 ? (
                  <LegacyStack spacing="extraTight">
                    {memberIds.map((memberId) => (
                      <Tag
                        key={memberId}
                        onRemove={handleRemoveMember(memberId)}
                      >
                        {nameFor(memberId)}
                      </Tag>
                    ))}
                  </LegacyStack>
                ) : undefined
              }
            />
          }
        >
          <Listbox onSelect={handleToggleMember}>{memberOptionsMarkup}</Listbox>
        </Combobox>

        <Select
          label={t("head")}
          options={headOptions}
          value={head}
          onChange={(value) => {
            // Data access belongs to a person: a new head starts without it,
            // the saved head gets its saved value back.
            if (value !== head) {
              setHeadHasDataAccess(
                accessAfterHeadChange(value, savedHead, savedHeadHadAccess),
              );
            }
            setHead(value);
          }}
          helpText={t("head_help") || undefined}
        />

        <label
          className={`flex items-start gap-2 text-sm ${accessBlock ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
          title={accessBlockTitle}
        >
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
            checked={!!head && headHasDataAccess}
            disabled={!!accessBlock}
            onChange={(event) => setHeadHasDataAccess(event.target.checked)}
          />
          <span>
            <span className="font-medium text-gray-900">{t("department_head_data_access")}</span>
            <span className="block text-xs text-gray-500">{t("department_head_data_access_hint")}</span>
            {savedHeadHadAccess && head !== savedHead && (
              <span className="mt-1 block text-xs text-amber-700">
                {t("department_head_data_access_regrant")}
              </span>
            )}
            {isGrantingAccess(accessState) && (
              <span className="mt-1 block text-xs text-amber-700">
                {visibleCount != null
                  ? t("department_head_data_access_scope_count", { n: visibleCount })
                  : t("department_head_data_access_scope_generic")}
              </span>
            )}
          </span>
        </label>

        <Button
          onClick={handleFormSubmit}
          disabled={!name.trim() || !isReady}
          loading={isLoading}
        >
          {t("save")}
        </Button>
      </FormLayout>
    </Form>
  );
};

export default DepartmentForm;
